var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { AxiosError } from "axios";
import { isEmail } from "class-validator";
import { formatUSOnlyPhoneNumber, formatUSPhoneForDisplay } from "healthex-common/phone.number.utils";
import { delayAndWait } from "healthex-common/utils";
import { VERIFICATION_COOLDOWN_SECONDS, VERIFICATION_DEFAULT_OTP_LENGTH } from "healthex-common/verification.dtos";
import { computed, observable, runInAction } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
import { getVerifyOTPErrorMessage, SendOTPState, VerifyOTPState } from "./VerifyOTPManager";
const t = getObservableT("patients");
export var AddContactStep;
(function (AddContactStep) {
    AddContactStep["ENTER_CONTACT"] = "ENTER_CONTACT";
    AddContactStep["VERIFY_OTP"] = "VERIFY_OTP";
})(AddContactStep || (AddContactStep = {}));
const VERIFYING_MIN_DURATION_MS = 1000;
const SEND_OUTCOME_HOLD_MS = 2000;
export class AddContactMethodOTPManager {
    constructor(patientService, getPatientId, contactType = "email") {
        this.patientService = patientService;
        this.getPatientId = getPatientId;
        this.formModel = new FormModel({
            contactValue: "",
            verificationCode: "",
        }, ["contactValue", "verificationCode"]);
        this.step = AddContactStep.ENTER_CONTACT;
        this.verifyOTPState = VerifyOTPState.INITIAL;
        this.sendOTPState = SendOTPState.INITIAL;
        this.enterContactError = null;
        this.pendingContactValue = null;
        this.addedEmail = null;
        this.addedPhone = null;
        this.verifyErrorOverride = null;
        this.resendCooldownIntervalId = null;
        this.disableResendTimeoutId = null;
        this.sendOutcomeHoldTimeoutId = null;
        this.pendingRateLimitCooldownSeconds = null;
        this.flowToken = 0;
        this.resendCooldownDurationSeconds = VERIFICATION_COOLDOWN_SECONDS;
        this.resendCooldownSecondsLeft = this.resendCooldownDurationSeconds;
        this.contactType = contactType;
    }
    setContactType(contactType) {
        if (this.contactType === contactType)
            return;
        this.flowToken++;
        this.clearResendTimers();
        runInAction(() => {
            this.contactType = contactType;
            this.step = AddContactStep.ENTER_CONTACT;
            this.verifyOTPState = VerifyOTPState.INITIAL;
            this.sendOTPState = SendOTPState.INITIAL;
            this.enterContactError = null;
            this.verifyErrorOverride = null;
            this.pendingContactValue = null;
            this.addedEmail = null;
            this.addedPhone = null;
            this.pendingRateLimitCooldownSeconds = null;
            this.resendCooldownSecondsLeft = this.resendCooldownDurationSeconds;
            this.formModel.updateInput({ contactValue: "", verificationCode: "" }, false);
        });
    }
    get sentToContact() {
        var _a;
        if (this.pendingContactValue && this.contactType === "phone") {
            return formatUSPhoneForDisplay(this.pendingContactValue);
        }
        return (_a = this.pendingContactValue) !== null && _a !== void 0 ? _a : "";
    }
    get verifyStatusMessage() {
        var _a;
        return (_a = this.verifyErrorOverride) !== null && _a !== void 0 ? _a : getVerifyOTPErrorMessage(this.verifyOTPState);
    }
    get isVerifyError() {
        return (this.verifyErrorOverride !== null ||
            this.verifyOTPState === VerifyOTPState.INCORRECT_CODE ||
            this.verifyOTPState === VerifyOTPState.TOO_MANY_ATTEMPTS);
    }
    get isVerifyTerminal() {
        return this.verifyErrorOverride !== null;
    }
    get canSendCode() {
        const value = this.formModel.input.contactValue.trim();
        if (this.contactType === "email")
            return isEmail(value);
        return validators.validPhone(value) && value !== "";
    }
    updateContactValue(value) {
        runInAction(() => {
            this.formModel.updateInput({ contactValue: value }, false);
            this.enterContactError = this.contactValueError();
        });
    }
    contactValueError() {
        if (this.contactType === "email")
            return null;
        const raw = this.formModel.input.contactValue.trim();
        const digits = raw.replace(/\D/g, "");
        if (digits.length < 10)
            return null;
        return formatUSOnlyPhoneNumber(raw) ? null : t("Please enter a valid US phone number to continue");
    }
    reset() {
        this.flowToken++;
        this.clearResendTimers();
        runInAction(() => {
            this.step = AddContactStep.ENTER_CONTACT;
            this.verifyOTPState = VerifyOTPState.INITIAL;
            this.sendOTPState = SendOTPState.INITIAL;
            this.enterContactError = null;
            this.verifyErrorOverride = null;
            this.pendingContactValue = null;
            this.addedEmail = null;
            this.addedPhone = null;
            this.resendCooldownSecondsLeft = this.resendCooldownDurationSeconds;
            this.pendingRateLimitCooldownSeconds = null;
            this.formModel.updateInput({ contactValue: "", verificationCode: "" }, false);
        });
    }
    isStaleFlow(flowToken) {
        return flowToken !== this.flowToken;
    }
    normalizeContactValue() {
        var _a;
        const raw = ((_a = this.pendingContactValue) !== null && _a !== void 0 ? _a : this.formModel.input.contactValue).trim();
        if (this.contactType === "email") {
            const lowered = raw.toLowerCase();
            return isEmail(lowered) ? lowered : null;
        }
        return formatUSOnlyPhoneNumber(raw);
    }
    async sendVerificationCode() {
        if (this.sendOTPState === SendOTPState.SENDING)
            return false;
        const patientId = this.getPatientId();
        if (!patientId)
            return false;
        if (this.step === AddContactStep.VERIFY_OTP && this.isVerifyTerminal)
            return false;
        const contact = this.normalizeContactValue();
        if (!contact) {
            runInAction(() => {
                this.enterContactError =
                    this.contactType === "email" ? t("Enter a valid email address") : t("Please enter a valid US phone number to continue");
            });
            return false;
        }
        runInAction(() => {
            this.sendOTPState = SendOTPState.SENDING;
            this.enterContactError = null;
            this.verifyOTPState = VerifyOTPState.INITIAL;
            this.verifyErrorOverride = null;
        });
        const isFirstSend = this.step === AddContactStep.ENTER_CONTACT;
        const flowToken = this.flowToken;
        try {
            if (this.contactType === "email") {
                await this.patientService.sendAddEmailOtp(patientId, contact);
            }
            else {
                await this.patientService.sendAddPhoneOtp(patientId, contact);
            }
            if (this.isStaleFlow(flowToken))
                return false;
            runInAction(() => {
                this.pendingContactValue = contact;
                this.step = AddContactStep.VERIFY_OTP;
                this.sendOTPState = isFirstSend ? SendOTPState.DISABLED : SendOTPState.SUCCESS;
                this.formModel.updateInput({ verificationCode: "" }, false);
            });
            this.startResendCooldown(isFirstSend ? 0 : SEND_OUTCOME_HOLD_MS);
            return true;
        }
        catch (err) {
            if (this.isStaleFlow(flowToken))
                return false;
            const code = this.errorCode(err);
            if (code === "rate_limited") {
                const retryAfter = this.retryAfterSeconds(err);
                if (retryAfter !== null)
                    this.pendingRateLimitCooldownSeconds = retryAfter;
                runInAction(() => {
                    this.pendingContactValue = contact;
                    this.step = AddContactStep.VERIFY_OTP;
                    this.sendOTPState = SendOTPState.DISABLED;
                });
                this.startResendCooldown();
                return false;
            }
            const duplicateMessage = code === "email_already_added"
                ? t("This email is already added.")
                : code === "email_owned_by_another_account"
                    ? t("This email is linked to another HealthEx Wallet. Try a different email.")
                    : code === "phone_already_added"
                        ? t("This phone number is already added.")
                        : code === "phone_owned_by_another_account"
                            ? t("This phone number is linked to another HealthEx Wallet. Try a different number.")
                            : null;
            if (!isFirstSend && duplicateMessage !== null) {
                runInAction(() => {
                    this.sendOTPState = SendOTPState.INITIAL;
                    this.verifyErrorOverride = duplicateMessage;
                });
                return false;
            }
            runInAction(() => {
                this.sendOTPState = SendOTPState.ERROR;
                if (duplicateMessage !== null) {
                    this.enterContactError = duplicateMessage;
                }
                else if (code === "invalid_email") {
                    this.enterContactError = t("Enter a valid email address");
                }
                else if (code === "invalid_phone" || code === "invalid_us_phone_number") {
                    this.enterContactError = t("Please enter a valid US phone number to continue");
                }
                else if (code === "notification_dispatch_failed") {
                    this.enterContactError = t("Error sending code, please try again later");
                }
                else {
                    this.enterContactError = t("Something went wrong. Please try again.");
                }
            });
            if (!isFirstSend)
                this.startResendCooldown(SEND_OUTCOME_HOLD_MS);
            return false;
        }
    }
    async submitVerificationCode() {
        const patientId = this.getPatientId();
        const contact = this.pendingContactValue;
        const code = this.formModel.input.verificationCode.trim();
        if (!patientId || !contact)
            return false;
        if (code.length !== VERIFICATION_DEFAULT_OTP_LENGTH)
            return false;
        runInAction(() => {
            this.verifyOTPState = VerifyOTPState.VERIFYING;
            this.verifyErrorOverride = null;
        });
        const verifyStartedAt = Date.now();
        const ownershipErrorMessage = this.contactType === "email"
            ? t("This email is linked to another HealthEx Wallet. Try a different email.")
            : t("This phone number is linked to another HealthEx Wallet. Try a different number.");
        const flowToken = this.flowToken;
        try {
            if (this.contactType === "email") {
                const added = await this.patientService.verifyAndAddEmail(patientId, contact, code);
                if (!this.isStaleFlow(flowToken)) {
                    await this.holdVerifyingSpinner(verifyStartedAt);
                    if (!this.isStaleFlow(flowToken)) {
                        runInAction(() => {
                            this.addedEmail = added;
                        });
                    }
                }
            }
            else {
                const added = await this.patientService.verifyAndAddPhone(patientId, contact, code);
                if (!this.isStaleFlow(flowToken)) {
                    await this.holdVerifyingSpinner(verifyStartedAt);
                    if (!this.isStaleFlow(flowToken)) {
                        runInAction(() => {
                            this.addedPhone = added;
                        });
                    }
                }
            }
            return true;
        }
        catch (err) {
            if (this.isStaleFlow(flowToken))
                return false;
            const errCode = this.errorCode(err);
            runInAction(() => {
                if (errCode === "too_many_attempts") {
                    this.verifyOTPState = VerifyOTPState.TOO_MANY_ATTEMPTS;
                }
                else if (errCode === "email_owned_by_another_account" || errCode === "phone_owned_by_another_account") {
                    this.verifyOTPState = VerifyOTPState.INITIAL;
                    this.verifyErrorOverride = ownershipErrorMessage;
                }
                else if (errCode === "verification_in_progress") {
                    this.verifyOTPState = VerifyOTPState.INITIAL;
                    this.verifyErrorOverride = t("This code has already been used. Check your contact methods before requesting a new code.");
                }
                else {
                    this.verifyOTPState = VerifyOTPState.INCORRECT_CODE;
                }
            });
            return false;
        }
    }
    backToEnterContact() {
        this.flowToken++;
        this.clearResendTimers();
        runInAction(() => {
            this.step = AddContactStep.ENTER_CONTACT;
            this.verifyOTPState = VerifyOTPState.INITIAL;
            this.sendOTPState = SendOTPState.INITIAL;
            this.enterContactError = null;
            this.verifyErrorOverride = null;
            this.pendingContactValue = null;
            this.addedEmail = null;
            this.addedPhone = null;
            this.formModel.updateInput({ verificationCode: "" }, false);
        });
    }
    async holdVerifyingSpinner(verifyStartedAt) {
        const remainingMs = VERIFYING_MIN_DURATION_MS - (Date.now() - verifyStartedAt);
        if (remainingMs > 0)
            await delayAndWait(remainingMs);
    }
    startResendCooldown(holdMs = 0) {
        var _a;
        this.clearResendTimers();
        const duration = (_a = this.pendingRateLimitCooldownSeconds) !== null && _a !== void 0 ? _a : this.resendCooldownDurationSeconds;
        this.pendingRateLimitCooldownSeconds = null;
        if (holdMs > 0) {
            this.sendOutcomeHoldTimeoutId = window.setTimeout(() => {
                this.sendOutcomeHoldTimeoutId = null;
                this.beginResendCooldown(duration);
            }, holdMs);
            return;
        }
        this.beginResendCooldown(duration);
    }
    beginResendCooldown(duration) {
        runInAction(() => {
            this.sendOTPState = SendOTPState.DISABLED;
            this.resendCooldownSecondsLeft = duration;
        });
        this.resendCooldownIntervalId = window.setInterval(() => {
            runInAction(() => {
                this.resendCooldownSecondsLeft = Math.max(0, this.resendCooldownSecondsLeft - 1);
            });
        }, 1000);
        this.disableResendTimeoutId = window.setTimeout(() => {
            this.clearResendTimers();
            runInAction(() => {
                this.sendOTPState = SendOTPState.INITIAL;
                this.resendCooldownSecondsLeft = this.resendCooldownDurationSeconds;
            });
        }, duration * 1000);
    }
    clearResendTimers() {
        if (this.sendOutcomeHoldTimeoutId !== null) {
            window.clearTimeout(this.sendOutcomeHoldTimeoutId);
            this.sendOutcomeHoldTimeoutId = null;
        }
        if (this.resendCooldownIntervalId !== null) {
            window.clearInterval(this.resendCooldownIntervalId);
            this.resendCooldownIntervalId = null;
        }
        if (this.disableResendTimeoutId !== null) {
            window.clearTimeout(this.disableResendTimeoutId);
            this.disableResendTimeoutId = null;
        }
    }
    errorCode(err) {
        var _a;
        if (err instanceof AxiosError && ((_a = err.response) === null || _a === void 0 ? void 0 : _a.data) && typeof err.response.data === "object") {
            return err.response.data.code;
        }
        return undefined;
    }
    retryAfterSeconds(err) {
        var _a;
        if (err instanceof AxiosError && ((_a = err.response) === null || _a === void 0 ? void 0 : _a.data) && typeof err.response.data === "object") {
            const data = err.response.data;
            return typeof data.retryAfterSeconds === "number" ? data.retryAfterSeconds : null;
        }
        return null;
    }
}
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "contactType", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "step", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "verifyOTPState", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "sendOTPState", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "enterContactError", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "pendingContactValue", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "addedEmail", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "addedPhone", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "verifyErrorOverride", void 0);
__decorate([
    observable
], AddContactMethodOTPManager.prototype, "resendCooldownSecondsLeft", void 0);
__decorate([
    computed
], AddContactMethodOTPManager.prototype, "sentToContact", null);
__decorate([
    computed
], AddContactMethodOTPManager.prototype, "verifyStatusMessage", null);
__decorate([
    computed
], AddContactMethodOTPManager.prototype, "isVerifyError", null);
__decorate([
    computed
], AddContactMethodOTPManager.prototype, "isVerifyTerminal", null);
__decorate([
    computed
], AddContactMethodOTPManager.prototype, "canSendCode", null);
